import type { SessionSummary } from "@/core/cameraApi/schemas/events";
import { Button } from "@/modules/shadcn/ui/button";
import { DateTimePicker } from "@/modules/shadcn/ui/date-time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { Loader2Icon, RefreshCwIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatSessionLabel } from "../../utils/formatSessionLabel";

export const ALL_SESSIONS = "all";

export type PassedFilter = "all" | "passed" | "failed";

interface ReportsToolbarProps {
  sessions: SessionSummary[];
  sessionId: string;
  onSessionChange: (sessionId: string) => void;
  /** Date range scoping the export only — table filtering lives in the column headers. */
  exportFrom: Date | undefined;
  onExportFromChange: (date: Date | undefined) => void;
  exportTo: Date | undefined;
  onExportToChange: (date: Date | undefined) => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
  onExport: () => void;
  isExporting?: boolean;
}

export const ReportsToolbar = ({
  sessions,
  sessionId,
  onSessionChange,
  exportFrom,
  onExportFromChange,
  exportTo,
  onExportToChange,
  onRefresh,
  isRefreshing = false,
  onExport,
  isExporting = false,
}: ReportsToolbarProps) => {
  const { t } = useTranslation(["reports", "common"]);
  const selectedLabel =
    sessionId === ALL_SESSIONS
      ? t("toolbar.allSessions")
      : (() => {
          const session = sessions.find((s) => String(s.id) === sessionId);
          return session ? formatSessionLabel(session, t) : undefined;
        })();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={sessionId}
        onValueChange={(value) => {
          if (value !== null) onSessionChange(value);
        }}
      >
        {/* h-8! aligns with the sm buttons/pickers — the trigger's own
            data-[size=sm]:h-9 outweighs a plain h-8. */}
        <SelectTrigger size="sm" className="h-8! w-64">
          <SelectValue placeholder={t("toolbar.selectSession")}>
            {selectedLabel}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SESSIONS}>{t("toolbar.allSessions")}</SelectItem>
          {sessions.map((session) => (
            <SelectItem key={session.id} value={String(session.id)}>
              {formatSessionLabel(session, t)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DateTimePicker
        edge="from"
        value={exportFrom}
        onChange={onExportFromChange}
        placeholder={t("toolbar.from")}
      />
      <DateTimePicker
        edge="to"
        value={exportTo}
        onChange={onExportToChange}
        placeholder={t("toolbar.to")}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={onRefresh}
        aria-label={t("common:actions.refresh")}
      >
        <RefreshCwIcon className={isRefreshing ? "animate-spin" : undefined} />
      </Button>
      <Button size="sm" onClick={onExport} disabled={isExporting}>
        {isExporting && <Loader2Icon className="animate-spin" />}
        {t("common:actions.export")}
      </Button>
    </div>
  );
};
